import React from 'react';
import { useTranslation } from 'react-i18next';

function Page3({ formData, updateFormData, onNext, onBack }) {
  const { t } = useTranslation();

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    updateFormData({ [name]: checked });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!formData.householdEmployeeAcknowledged || !formData.recordKeepingAcknowledged) {
      alert('Please check both acknowledgement boxes to continue.');
      return;
    }
    onNext();
  };

  return (
    <form onSubmit={handleNext}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('page3.header.title')}
        </h1>
        <p className="text-gray-600">{t('page3.header.subtitle')}</p>
      </div>

      {/* Educational Content */}
      <div className="mb-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-gray-800 mb-4 leading-relaxed">
          {t('page3.content.definition')}
        </p>
        
        <p className="text-sm text-gray-800 mb-4 leading-relaxed">
          <strong>{t('common.example')}:</strong> {t('page3.content.example1')}
        </p>

        <p className="text-sm text-gray-800 mb-3 font-semibold">
          {t('page3.content.houseworkTitle')}
        </p>
        
        <div className="grid grid-cols-3 gap-2 mb-4">
          <ul className="text-sm text-gray-800 space-y-1">
            <li>• Babysitters</li>
            <li>• Caretakers</li>
            <li>• Cleaners</li>
            <li>• Domestic Workers</li>
          </ul>
          <ul className="text-sm text-gray-800 space-y-1">
            <li>• Drivers</li>
            <li>• Health Aides</li>
            <li>• Housekeepers</li>
            <li>• Maids</li>
          </ul>
          <ul className="text-sm text-gray-800 space-y-1">
            <li>• Nannies</li>
            <li>• Yard Workers</li>
            <li>• Private Nurses</li>
          </ul>
        </div>

        <p className="text-sm text-gray-800 leading-relaxed">
          {t('page3.content.selfEmployedDef')}
        </p>
        
        <p className="text-sm text-gray-800 mt-4 leading-relaxed">
          <strong>{t('common.example')}:</strong> {t('page3.content.example2')}
        </p>
      </div>

      {/* First Acknowledgement Checkbox */}
      <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            name="householdEmployeeAcknowledged"
            checked={formData.householdEmployeeAcknowledged || false}
            onChange={handleCheckboxChange}
            className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            required
          />
          <span className="ml-3 text-sm text-gray-800 leading-relaxed">
            {t('page3.content.acknowledgement1')} <span className="text-red-500">{t('common.required')}</span>
          </span>
        </label>
      </div>

      {/* Record Keeping Information */}
      <div className="mb-6 p-6 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="text-sm text-gray-800 mb-3 font-semibold">
          {t('page3.content.recordKeepingTitle')}
        </p>
        
        <ul className="text-sm text-gray-800 space-y-2 mb-4">
          <li>
            <strong>• Identify sources of income.</strong> You may receive money or property from a variety of sources. Your records can identify the sources of income. This information can be used to separate business vs. non-business income and taxable vs. non-taxable income.
          </li>
          <li>
            <strong>• Keep track of expenses.</strong> You may forget an expense unless you record it at the time of occurrence. The records can be used to identify expenses for which you claim a deduction and if you will be able to itemize deductions.
          </li>
          <li>
            <strong>• Keep track of the basis of property.</strong> You need to keep records that show the basis of your property. This includes the original cost of the other basis of the property and any improvements you've made.
          </li>
          <li>
            <strong>• Prepare tax returns.</strong> You recorded information to prepare your tax return. Good records can help you to file quickly and accurately.
          </li>
          <li>
            <strong>• Support reported information on tax returns.</strong> You should keep records in case a question arises about an item on your tax return. If the IRS examines your tax return, you may be asked to verify the items reported. Well-kept records will help you explain any item and arrive at the correct tax with minimal effort. If you do not have this information, you will have to spend time gathering statements and receipts from various sources. If you cannot produce documents, you may have to pay additional tax and be subject to penalties.
          </li>
        </ul>

        <p className="text-sm text-gray-800 font-semibold">
          {t('page3.content.threshold')}
        </p>
      </div>

      {/* Second Acknowledgement Checkbox */}
      <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            name="recordKeepingAcknowledged"
            checked={formData.recordKeepingAcknowledged || false}
            onChange={handleCheckboxChange}
            className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            required
          />
          <span className="ml-3 text-sm text-gray-800 leading-relaxed">
            {t('page3.content.acknowledgement2')} <span className="text-red-500">{t('common.required')}</span>
          </span>
        </label>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          className="w-1/2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-medium text-lg"
        >
          {t('common.back')}
        </button>
        <button
          type="submit"
          className="w-1/2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
        >
          {t('common.next')}
        </button>
      </div>
    </form>
  );
}

export default Page3;
